import { useEffect, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { fetchFaqs } from '../lib/api';

interface FAQItem {
  id: number;
  documentId: string;
  question: string;
  answer: any[] | string;
  createdAt?: string;
  updatedAt?: string;
  publishedAt?: string;
}

interface FAQMeta {
  pagination: { page: number; pageSize: number; pageCount: number; total: number };
}

// Scroll-triggered reveal
const useInView = (threshold = 0.1) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); io.disconnect(); } },
      { threshold, rootMargin: '0px 0px -30px 0px' }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold]);
  return { ref, inView };
};

const FAQItemCard: React.FC<{ faq: FAQItem; index: number; answerMd: string }> = ({ faq, index, answerMd }) => {
  const { ref, inView } = useInView();
  const [open, setOpen] = useState(false);
  return (
    <div
      ref={ref}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(12px)',
        transition: `opacity 0.5s ease-out ${Math.min(index * 0.04, 0.5)}s, transform 0.5s ease-out ${Math.min(index * 0.04, 0.5)}s`,
      }}
    >
      <details
        open={open}
        onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
        className={`group bg-white border rounded-2xl overflow-hidden transition-all duration-300 ${open ? 'border-primary shadow-md shadow-primary/5' : 'border-gray-100 hover:border-primary/30'}`}
      >
        <summary className="flex items-start justify-between gap-3 cursor-pointer px-5 py-4 list-none">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className={`mt-0.5 flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold transition-colors ${open ? 'bg-primary text-white' : 'bg-gray-50 text-gray-400 group-hover:bg-primary/10 group-hover:text-primary'}`}>
              {String(index + 1).padStart(2, '0')}
            </div>
            <h3 className={`text-sm md:text-base font-bold leading-snug transition-colors ${open ? 'text-primary' : 'text-gray-900'}`}>
              {faq.question || 'Untitled FAQ'}
            </h3>
          </div>
          <span className={`flex-shrink-0 w-8 h-8 rounded-full border flex items-center justify-center font-bold text-lg transition-all ${open ? 'bg-primary text-white border-primary rotate-45' : 'bg-white text-primary border-gray-200 group-hover:border-primary'}`}>
            +
          </span>
        </summary>
        <div className="px-5 pb-5 pl-[72px] text-gray-600 prose prose-sm max-w-none prose-headings:text-gray-900 prose-headings:font-bold prose-p:leading-relaxed prose-p:text-gray-600 prose-li:text-gray-600 prose-a:text-primary">
          <ReactMarkdown>{answerMd}</ReactMarkdown>
        </div>
      </details>
    </div>
  );
};

const FAQ = () => {
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [meta, setMeta] = useState<FAQMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const answerToMarkdown = (answer: any[] | string): string => {
    if (typeof answer === 'string') return answer;
    if (Array.isArray(answer)) {
      return answer
        .map((block) => {
          if (!block || typeof block !== 'object') return '';
          const childrenText = Array.isArray(block.children)
            ? block.children.map((child: any) => child?.text || '').join(' ')
            : '';
          switch (block.type) {
            case 'heading':
            case 'header':
              return `## ${childrenText}`;
            case 'paragraph':
            case 'text':
              return childrenText;
            case 'ordered-list':
              if (Array.isArray(block.children)) {
                return block.children.map((child: any, idx: number) => `${idx + 1}. ${child?.text || ''}`).join('\n');
              }
              return childrenText;
            case 'unordered-list':
            case 'list':
              if (Array.isArray(block.children)) {
                return block.children.map((child: any) => `- ${child?.text || ''}`).join('\n');
              }
              return childrenText;
            default:
              return childrenText;
          }
        })
        .filter(Boolean)
        .join('\n\n');
    }
    return '';
  };

  // Server returns already-filtered results; display all fetched items as-is.
  const filteredFaqs = faqs;

  const totalPages = meta?.pagination?.pageCount ?? 1;

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    const loadFaqs = async () => {
      try {
        setLoading(true);
        const response = await fetchFaqs(currentPage, pageSize, debouncedSearch);
        setFaqs(response.data);
        setMeta(response.meta);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load FAQ');
      } finally {
        setLoading(false);
      }
    };
    loadFaqs();
  }, [currentPage, pageSize, debouncedSearch]);

  const suggestedTopics = ['Carnivore', 'Ketovore', 'Macros', 'Recipes', 'Community'];

  return (
    <div className="bg-gray-50 min-h-screen">
      <Helmet>
        <title>FAQ — EatMeat</title>
        <meta name="description" content="Answers to common questions about EatMeat — the carnivore and ketovore diet app." />
      </Helmet>

      <style>{`
        @keyframes pulse-slow { 0%,100% { opacity: 0.25; } 50% { opacity: 0.5; } }
        @keyframes fadeIn { 0% { opacity: 0; transform: translateY(12px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes shimmerText { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        .shimmer-faq { background: linear-gradient(90deg, #f97316 0%, #ef4444 30%, #f97316 60%, #ef4444 100%); background-size: 200% 100%; -webkit-background-clip: text; background-clip: text; color: transparent; animation: shimmerText 6s linear infinite; }
      `}</style>

      {/* Page header strip */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-4 md:py-5">
          <div className="flex items-center gap-3">
            <span className="text-2xl">❓</span>
            <div>
              <h1 className="text-lg md:text-xl font-extrabold tracking-tight leading-tight text-gray-900">FAQ</h1>
              <p className="text-gray-500 text-xs">Everything you need to know about EatMeat</p>
            </div>
          </div>
        </div>
      </div>

      {/* Hero with search */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-20 -left-20 w-80 h-80 bg-primary rounded-full blur-3xl opacity-15" style={{ animation: 'pulse-slow 9s ease-in-out infinite' }} />
        <div className="pointer-events-none absolute top-20 -right-20 w-96 h-96 bg-red-200 rounded-full blur-3xl opacity-20" style={{ animation: 'pulse-slow 11s ease-in-out infinite 1s' }} />

        <div className="relative max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14 text-center">
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1 text-xs font-semibold text-gray-700 mb-4 shadow-sm" style={{ animation: 'fadeIn 0.6s ease-out' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> {meta?.pagination?.total ?? 0} questions answered
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 tracking-tight leading-[1.1] mb-4" style={{ animation: 'fadeIn 0.7s ease-out 0.1s backwards' }}>
            How can we <span className="shimmer-faq">help</span>?
          </h2>
          <p className="text-gray-500 text-base md:text-lg mb-6" style={{ animation: 'fadeIn 0.7s ease-out 0.2s backwards' }}>
            Search our FAQ or browse by topic below.
          </p>

          {/* Search */}
          <div className="relative max-w-xl mx-auto" style={{ animation: 'fadeIn 0.7s ease-out 0.3s backwards' }}>
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
            </svg>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="Search FAQs — try 'macros', 'carnivore', 'subscription'…"
              className="w-full bg-white border border-gray-200 rounded-2xl pl-12 pr-12 py-3.5 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-primary/20 focus:border-primary shadow-sm transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => { setSearchTerm(''); setCurrentPage(1); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          {/* Suggested topics */}
          {!searchTerm && (
            <div className="flex flex-wrap justify-center gap-2 mt-5" style={{ animation: 'fadeIn 0.7s ease-out 0.4s backwards' }}>
              <span className="text-xs text-gray-400 font-semibold uppercase tracking-wider self-center">Popular:</span>
              {suggestedTopics.map((t) => (
                <button
                  key={t}
                  onClick={() => setSearchTerm(t)}
                  className="px-3 py-1 bg-white border border-gray-200 rounded-full text-xs font-semibold text-gray-600 hover:border-primary hover:text-primary transition-colors"
                >
                  {t}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ list */}
      <section className="relative max-w-3xl mx-auto px-4 md:px-6 pb-16">
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 bg-white border border-gray-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-red-700 text-sm">{error}</div>
        ) : filteredFaqs.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-3 opacity-30">🔍</div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">No matches for "{searchTerm}"</h3>
            <p className="text-gray-500 mb-5">Try a different search term or contact us.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => setSearchTerm('')} className="px-6 py-2.5 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors">
                Clear search
              </button>
              <Link to="/contact" className="px-6 py-2.5 border border-gray-200 bg-white text-gray-700 rounded-xl font-semibold hover:border-primary hover:text-primary transition-colors">
                Contact us
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {filteredFaqs.map((faq, i) => (
                <FAQItemCard key={faq.id} faq={faq} index={(currentPage - 1) * pageSize + i} answerMd={answerToMarkdown(faq.answer)} />
              ))}
            </div>

            {/* Pagination — only when not searching */}
            {!searchTerm && totalPages > 1 && (
              <div className="mt-8 bg-white border border-gray-100 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-xs text-gray-500">
                  Showing {filteredFaqs.length} of <span className="font-bold text-gray-700">{meta?.pagination?.total ?? filteredFaqs.length}</span> · Page {currentPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <select
                    id="pageSize"
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-semibold text-gray-700 focus:outline-none focus:border-primary"
                  >
                    <option value={6}>6 per page</option>
                    <option value={12}>12 per page</option>
                    <option value={24}>24 per page</option>
                    <option value={100}>All</option>
                  </select>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-600 font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    ‹
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-600 font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* Still have questions CTA */}
      <section className="relative bg-white border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-orange-500 text-white text-2xl shadow-lg shadow-primary/20 mb-4">
            💬
          </div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight mb-2">Still have questions?</h2>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">Can't find the answer? Reach out to our support team — we reply within 24 hours.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/contact" className="group px-6 py-3 bg-primary text-white rounded-xl font-semibold shadow-md shadow-primary/20 hover:shadow-lg hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2">
              Contact support
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </Link>
            <a href="mailto:support@eatmeatapp.com" className="px-6 py-3 border border-gray-200 bg-white text-gray-700 rounded-xl font-semibold hover:border-primary hover:text-primary transition-colors">
              support@eatmeatapp.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
